import { useRef, useState } from 'react';
import type { IAccountView, ILoginSuccessView, MfaMethod } from '@shared/services';
import {
  authClient,
  isAccountsProof,
  isMfaEnrollmentChallenge,
  isMfaEnrollmentChallengeView,
  isMfaVerifyChallenge,
  isMfaVerifyChallengeView,
  isPasswordChangeChallenge,
  useCheckCredentialsMutation,
  useLoginMutation,
  type IMfaConfirmRestView,
  type LoginRequestParams,
  type MfaVerifyResponse,
} from '@/shared/api/auth';
import { type MfaSetupExitReason, type MfaSetupResult } from '@/modules/auth/features/mfa/useMfaSetup';
import { filterKnownMfaMethods } from '@/modules/auth/utils/mfaMethods';
import { redirectToPostAuthDestination } from '@/shared/auth/postAuthRedirect';
import { getLoginFlowError, isUnauthorizedError } from '@/shared/utils';

/**
 * The linear wizard steps. `done` renders a full-screen loader: the session is adopted and the
 * full-page redirect is underway, but the old document stays visible until the browser swaps it —
 * without a terminal step the credential form would flash during that gap.
 */
export type LoginStep = 'credentials' | 'mfa' | 'mfa-setup' | 'update-password' | 'pick' | 'done';

/** The inline MFA challenge to satisfy on the `mfa` (verify) or `mfa-setup` (enrollment) step. */
export type MfaChallenge = {
  mfaPending: string;
  mfaMethods: MfaMethod[];
};

export type AccountSelection = {
  userId: number;
  locationId?: number;
};

const NO_ACCOUNTS_MESSAGE = 'You have no accounts to login to';
const PROOF_EXPIRED_MESSAGE = 'Your session expired. Please sign in again.';
const PASSWORD_CHANGED_MESSAGE = 'Your password has been updated. Please sign in with your new password.';

/**
 * The MFA-first login wizard: `credentials → [mfa | mfa-setup] → [pick] → [update-password] → done`,
 * where every bracketed step is conditionally skipped. Every login-originated challenge is an inline
 * step on `/login` — the browser URL never changes, so the wizard's in-memory state survives all of
 * them and credentials are entered exactly once:
 *
 * - The identity-scoped MFA challenge is raised at `check-credentials` (before account selection);
 *   the verify OR enrollment ceremony mints an `accountsProof` (not a session) that is threaded
 *   through a SECOND `check-credentials` (to reveal the list) and the final `login` (to skip a second
 *   challenge).
 * - A `password_change_required` challenge from `login` runs the forced change inline; on success the
 *   wizard updates its in-memory password and silently re-issues `login` with the remembered account
 *   selection — no re-authentication.
 *
 * All of `email`, `password`, `accountsProof`, and `changeToken` live in memory only (refs/state) —
 * never the URL, router state, or storage. A reload therefore restarts at the credential step.
 * `login`'s own outcome handling (password-change, MFA re-challenge) is retained as a fallback for
 * the no-proof path and for the silent re-login.
 *
 * Any `returnUrl` needs no handling here: it rides in the `/login` URL query, which the wizard never
 * leaves, and is read there by `redirectToPostAuthDestination` at the very end.
 */
export const useLoginWizard = () => {
  const { checkCredentials, isCheckingCredentials } = useCheckCredentialsMutation();
  const { login: loginRequest, isLoggingIn } = useLoginMutation();

  const [step, setStep] = useState<LoginStep>('credentials');
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  const [accounts, setAccounts] = useState<IAccountView[]>([]);
  const [error, setError] = useState<string | null>(null);

  const emailRef = useRef<string | null>(null);
  const passwordRef = useRef<string | null>(null);
  const accountsProofRef = useRef<string | null>(null);
  // The forced-change token from a `password_change_required` outcome. A live credential: memory
  // only, never router state or the URL (unlike the switch flow, which may use history state).
  const changeTokenRef = useRef<string | null>(null);
  // The last selection submitted to `login`, replayed by the silent re-login after a password change
  // so the user is not asked to pick again. `undefined` means the single-account auto-login shape.
  const selectionRef = useRef<AccountSelection | undefined>(undefined);
  // Serialises the async steps so a double-click cannot fire two `check-credentials`/`login` calls.
  const pendingRef = useRef(false);

  // A `401` from a proof-bearing call (second `check-credentials` or `login`) means the short-lived
  // proof is stale and cannot be renewed — clear everything and return to the credential form. Also
  // the landing point for every inline-step exit (cancel, Back, expired token).
  const resetToCredentials = (message?: string) => {
    emailRef.current = null;
    passwordRef.current = null;
    accountsProofRef.current = null;
    changeTokenRef.current = null;
    selectionRef.current = undefined;
    setMfaChallenge(null);
    setAccounts([]);
    setError(message ?? null);
    setStep('credentials');
  };

  /** Terminal success: adopt the session and hold the `done` loader while the redirect completes. */
  const completeLogin = (response: ILoginSuccessView) => {
    setStep('done');
    authClient.adoptSession(response);
    redirectToPostAuthDestination();
  };

  const toMfaSetupStep = (challenge: { mfaPending?: string; mfaMethods?: string[] }) => {
    setMfaChallenge({
      mfaPending: challenge.mfaPending ?? '',
      mfaMethods: filterKnownMfaMethods(challenge.mfaMethods ?? []),
    });
    setStep('mfa-setup');
  };

  /**
   * Issue the final `login`, carrying `accountsProof` when the flow cleared MFA at credential check.
   * `isSilentRetry` marks the re-login after an inline password change: its failures reset to the
   * credential step (the spent `changeToken` cannot be retried, and the step showing then has no
   * error surface), while a challenge outcome lands on the corresponding inline step as usual.
   */
  const doLogin = async (selection?: AccountSelection, { isSilentRetry = false } = {}) => {
    const email = emailRef.current;
    const password = passwordRef.current;

    if (!email || !password) {
      throw new Error('Email or password is missing');
    }

    selectionRef.current = selection;

    const params: LoginRequestParams = { email, password };

    if (selection?.userId !== undefined) {
      params.userId = selection.userId;
    }

    if (selection?.locationId !== undefined) {
      params.locationId = selection.locationId;
    }

    // Present the proof so the backend does not raise a second MFA challenge. Omitted when no MFA was
    // required, preserving the legacy request shape.
    const hadProof = accountsProofRef.current !== null;
    if (hadProof) {
      params.accountsProof = accountsProofRef.current ?? undefined;
    }

    try {
      const response = await loginRequest(params);

      // Retained `login` fallbacks. A forced password-change comes only from `login`, never from
      // `check-credentials`; run it as an inline step, holding the `changeToken` in memory.
      if (isPasswordChangeChallenge(response)) {
        changeTokenRef.current = response.changeToken;
        setStep('update-password');

        return;
      }

      // `login` may still re-challenge for MFA when called without a valid proof (the no-proof path,
      // or a silent re-login whose proof did not survive the password rotation): both enrollment and
      // verification stay inline.
      if (isMfaEnrollmentChallenge(response)) {
        toMfaSetupStep(response);

        return;
      }

      if (isMfaVerifyChallenge(response)) {
        setMfaChallenge({ mfaPending: response.mfaPending, mfaMethods: filterKnownMfaMethods(response.mfaMethods) });
        setStep('mfa');

        return;
      }

      completeLogin(response);
    } catch (caught) {
      // A rejected silent re-login cannot surface on the (already-completed) change form; return to
      // the credential form — with the sign-in-again message for a `401`, or the failure itself.
      if (isSilentRetry) {
        resetToCredentials(isUnauthorizedError(caught) ? PASSWORD_CHANGED_MESSAGE : getLoginFlowError(caught));

        return;
      }

      // A stale proof at `login` resets the flow; any other failure surfaces inline on the current step.
      if (hadProof && isUnauthorizedError(caught)) {
        resetToCredentials(PROOF_EXPIRED_MESSAGE);

        return;
      }

      setError(getLoginFlowError(caught));
    }
  };

  /**
   * Reveal the account list. The FIRST call (no proof) branches on `outcome` before inspecting `list`.
   * The SECOND call (with proof, after MFA) does the same but resets on a `401` (stale proof).
   */
  const revealAccounts = async (withProof: boolean) => {
    const email = emailRef.current;
    const password = passwordRef.current;

    if (!email || !password) {
      throw new Error('Email or password is missing');
    }

    const view = await checkCredentials({
      email,
      password,
      ...(withProof && accountsProofRef.current ? { accountsProof: accountsProofRef.current } : {}),
    });

    // Branch on `outcome` first (D1): an empty `list` WITH an `outcome` is a withheld list (an MFA
    // challenge), not "no accounts". Challenges only arise on the first, proof-less call.
    if (isMfaEnrollmentChallengeView(view)) {
      toMfaSetupStep(view);

      return;
    }

    if (isMfaVerifyChallengeView(view)) {
      setMfaChallenge({
        mfaPending: view.mfaPending ?? '',
        mfaMethods: filterKnownMfaMethods(view.mfaMethods ?? []),
      });
      setStep('mfa');

      return;
    }

    // No `outcome`: interpret the `list`. Empty means no accounts; one auto-submits; several show the
    // in-page picker. A populated list carries `accountsProof` through to `login` only when we hold one.
    if (view.list.length === 0) {
      setError(NO_ACCOUNTS_MESSAGE);

      return;
    }

    setAccounts(view.list);

    if (view.list.length === 1) {
      await doLogin();

      return;
    }

    setStep('pick');
  };

  /** Step 1: submit credentials. */
  const submitCredentials = async (email: string, password: string) => {
    if (pendingRef.current) {
      return;
    }

    pendingRef.current = true;
    setError(null);

    try {
      emailRef.current = email;
      passwordRef.current = password;
      await revealAccounts(false);
    } catch (caught) {
      // The first `check-credentials` carries no proof, so a `401` here is just bad credentials —
      // surface it inline and stay on the form (no reset).
      setError(getLoginFlowError(caught));
    } finally {
      pendingRef.current = false;
    }
  };

  /**
   * Step 2 completion. The identity-scoped ceremony yields an `accountsProof` (no session): capture it
   * and re-call `check-credentials` to reveal the list. A session here would be the account-scoped
   * fallback (it does not occur on this path) — adopt it defensively.
   */
  const onVerified = async (response: MfaVerifyResponse) => {
    if (pendingRef.current) {
      return;
    }

    pendingRef.current = true;
    setError(null);

    try {
      if (!isAccountsProof(response)) {
        completeLogin(response);

        return;
      }

      accountsProofRef.current = response.accountsProof;
      await revealAccounts(true);
    } catch (caught) {
      if (isUnauthorizedError(caught)) {
        resetToCredentials(PROOF_EXPIRED_MESSAGE);

        return;
      }

      setError(getLoginFlowError(caught));
    } finally {
      pendingRef.current = false;
    }
  };

  /**
   * Inline enrollment (`mfa-setup` step) completion. The identity-scoped confirm carries the
   * `accountsProof` — as a sibling field on the confirm view (TOTP/email) or as the discriminated
   * accounts outcome (passkey). Capture it and continue exactly like `onVerified`: the second
   * `check-credentials` reveals the list without re-entering credentials. A proof-less session is the
   * account-scoped fallback — adopt it.
   */
  const onEnrollmentComplete = async (result: MfaSetupResult) => {
    if (pendingRef.current) {
      return;
    }

    pendingRef.current = true;
    setError(null);

    try {
      const proof = isAccountsProof(result) ? result.accountsProof : (result as IMfaConfirmRestView).accountsProof;

      if (!proof) {
        completeLogin(result as IMfaConfirmRestView);

        return;
      }

      accountsProofRef.current = proof;
      await revealAccounts(true);
    } catch (caught) {
      if (isUnauthorizedError(caught)) {
        resetToCredentials(PROOF_EXPIRED_MESSAGE);

        return;
      }

      setError(getLoginFlowError(caught));
    } finally {
      pendingRef.current = false;
    }
  };

  /** Any unfinished exit from the inline enrollment step: back to a fresh credential form. */
  const onEnrollmentExit = (reason: MfaSetupExitReason) => {
    resetToCredentials(reason === 'expired' ? PROOF_EXPIRED_MESSAGE : undefined);
  };

  /**
   * Inline forced password-change success (D3): remember the new password and silently re-issue
   * `login` with the remembered selection (and any held proof) — the user signs in exactly once. A
   * further challenge from this `login` lands on the corresponding inline step via `doLogin`'s
   * regular outcome handling; a `401` resets to the credential form with a sign-in-again message.
   */
  const onPasswordChanged = async (newPassword: string) => {
    if (pendingRef.current) {
      return;
    }

    pendingRef.current = true;
    setError(null);
    passwordRef.current = newPassword;
    // The (spent) changeToken is deliberately NOT cleared here: the `update-password` step keeps
    // rendering the form — its button spinning — while the silent `login` is in flight, and the
    // token is discarded by `resetToCredentials` or made irrelevant by the step that follows.

    try {
      await doLogin(selectionRef.current, { isSilentRetry: true });
    } finally {
      pendingRef.current = false;
    }
  };

  /** The expired-token exit of the inline forced-change step. */
  const onPasswordChangeExpired = () => resetToCredentials(PROOF_EXPIRED_MESSAGE);

  /** The voluntary "Back to login" exit of the inline forced-change step. */
  const onPasswordChangeBack = () => resetToCredentials();

  /** Step 3: submit the chosen account (and location) to `login`, carrying any `accountsProof`. */
  const selectAccount = async (selection: AccountSelection) => {
    if (pendingRef.current) {
      return;
    }

    pendingRef.current = true;
    setError(null);

    try {
      await doLogin(selection);
    } catch (caught) {
      setError(getLoginFlowError(caught));
    } finally {
      pendingRef.current = false;
    }
  };

  return {
    step,
    mfaChallenge,
    accounts,
    error,
    // Read at render time by the `update-password` step; set before the step becomes visible.
    changeToken: changeTokenRef.current,
    isPending: isCheckingCredentials || isLoggingIn,
    submitCredentials,
    onVerified,
    onEnrollmentComplete,
    onEnrollmentExit,
    onPasswordChanged,
    onPasswordChangeExpired,
    onPasswordChangeBack,
    selectAccount,
    resetToCredentials,
  };
};
